import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';
import { getFileIcon, parseFilename } from '../utils/referenceParser';

/**
 * Enhanced Reference Drawer Component
 * Shows detailed information about a specific reference/source
 */
const ReferenceDrawer = ({ isOpen, onClose, reference, workspaceId }) => {
  const navigate = useNavigate();
  const [dataset, setDataset] = useState(null);
  const [, setLoadingDataset] = useState(false);
  const [, setSourceContent] = useState(null);
  const [, setLoadingContent] = useState(false);

  const filenameInfo = parseFilename(reference?.filename);
  const fileIcon = getFileIcon(reference?.filename, reference?.type);

  useEffect(() => {
    if (!isOpen || !reference?.filename || !workspaceId) {
      return;
    }

    const findDatasetAndContent = async () => {
      setLoadingDataset(true);
      setLoadingContent(true);

      try {
        const token = localStorage.getItem('directus_token');
        if (!token) {
          return;
        }

        const response = await axios.get(
          `${DIRECTUS_INSTANCE}/items/data_sets`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            params: {
              'filter[linked_workspace][_eq]': workspaceId,
              fields: '*'
            }
          }
        );

        if (response.data?.data) {
          const matchingDataset = response.data.data.find((datasetEntry) => {
            const datasetName = datasetEntry.name || '';
            const sourceFilename = reference.filename || '';

            return (
              datasetName === sourceFilename
              || datasetName.includes(filenameInfo.baseName)
              || filenameInfo.baseName.includes(datasetName.replace(/\.[^/.]+$/, ''))
              || sourceFilename.includes(datasetName)
              || datasetName.includes(sourceFilename)
            );
          });

          if (matchingDataset) {
            setDataset(matchingDataset);

            if (matchingDataset.file_content || matchingDataset.content) {
              setSourceContent({
                content: matchingDataset.file_content || matchingDataset.content,
                type: 'dataset'
              });
            }
          }
        }
      } catch (error) {
        console.error('Error finding dataset:', error);
      } finally {
        setLoadingDataset(false);
        setLoadingContent(false);
      }
    };

    findDatasetAndContent();
  }, [isOpen, reference?.filename, workspaceId, filenameInfo.baseName]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </Transition.Child>

            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                    <div className="px-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl" role="img" aria-label="file icon">
                            {fileIcon}
                          </span>
                          <div>
                            <Dialog.Title className="text-xl font-medium text-gray-900">
                              Reference Details
                            </Dialog.Title>
                            <p className="text-sm text-gray-600">
                              {reference?.filename || reference?.displayText || 'No reference selected'}
                            </p>
                          </div>
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={onClose}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="relative mt-6 flex-1 px-4 sm:px-6">
                      {!reference ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No reference data available.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="border p-4 rounded">
                            <h3 className="font-semibold mb-2">File Information</h3>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium">Filename:</span>
                                <p className="font-mono">{reference.filename || reference.displayText}</p>
                              </div>
                              {reference.type && (
                                <div>
                                  <span className="font-medium">Type:</span>
                                  <p>{reference.type.replace('_', ' ')}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {dataset && (
                            <div className="border p-3 rounded">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{dataset.name}</p>
                                  <p className="text-sm text-gray-600">Dataset</p>
                                </div>
                                <button
                                  onClick={() => navigate(`/datasets/details/${dataset.id}`)}
                                  className="text-blue-600 hover:underline text-sm"
                                >
                                  View →
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ReferenceDrawer;
